/**
 * Admin Portal → Partners → a tag → Email kit (partner launch UC3, workstream S5).
 *
 * docs/plans/PARTNER_LAUNCH_PLAN_2026_10_17.md §1 UC3: a partner puts the tag's shared code in
 * their OWN email. The kit is what we hand them: the code, the offer line, plain App Store and
 * Google Play links and the redeem steps — as HTML for their email tool and as plain text. No
 * website hop, no cookie: the person installs from the store, signs up and types the code.
 *
 * Pure (no DOM, no React) so jest pins every byte. Like the website code (websiteCode.ts, whose
 * escaping it reuses), the HTML loads nothing at runtime — no script, stylesheet, font or image —
 * and every value is HTML-escaped where it lands. Always "Coach Cedar", never bare "Cedar".
 *
 * The store links are the plain listings the app itself shares (ReferralDrawer's STORE_LINK,
 * routers/public_minimum_version.py): no campaign or referrer parameters — a code carries the
 * attribution here, not the link. Apple's link has no storefront in it, so a reader anywhere
 * lands on their own country's App Store.
 */

import { CODE_RE, type TagRow } from './partnersLogic';
import { escapeHtml } from './websiteCode';

export const APP_STORE_URL = 'https://apps.apple.com/app/coach-cedar/id6758776684';
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.coachcedar.app';

/** Worded for build 249 (plan §1 UC3 step 3). */
export const REDEEM_STEPS =
  'Open the app → Account → Redeem code (or Account → Train with a friend → Redeem code once your free week has started)';

type OfferFields = Pick<TagRow, 'trial_days' | 'discount_monthly_pct' | 'discount_annual_pct'> & {
  discount_monthly_months?: number | null;
};

function firstMonths(months: number | null | undefined): string {
  const m = Number(months ?? 12);
  return m === 1 ? 'your first month' : `your first ${m} months`;
}

/**
 * What the tag gives, as the person reads it — or null for a tracking-only tag (the 7-day free
 * week is everyone's; it is not an offer). Mirrors the onboarding line's wording (plan §1, E10b).
 */
export function offerLine(tag: OfferFields): string | null {
  const trial = tag.trial_days === 30 ? 'your first month of coaching free'
    : tag.trial_days === 14 ? 'your first 2 weeks of coaching free'
      : null;
  const discounts: string[] = [];
  if (tag.discount_monthly_pct != null) {
    discounts.push(`${tag.discount_monthly_pct}% off ${firstMonths(tag.discount_monthly_months)} on a monthly plan`);
  }
  if (tag.discount_annual_pct != null) discounts.push(`${tag.discount_annual_pct}% off your first year on an annual plan`);
  const discount = discounts.join(', or ');
  if (trial && discount) return `${trial}, then ${discount}`;
  return trial ?? (discount || null);
}

/** "Valid until November 30, 2026. Limited to the first 500 people." — only what the code has. */
export function kitExpiryText(expiresAt: string | null | undefined, maxRedemptions: number | null | undefined, timeZone?: string): string {
  const parts: string[] = [];
  if (expiresAt) {
    const d = new Date(expiresAt);
    if (!Number.isNaN(d.getTime())) {
      const day = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone }).format(d);
      parts.push(`Valid until ${day}.`);
    }
  }
  if (maxRedemptions != null && Number(maxRedemptions) > 0) {
    const n = Number(maxRedemptions);
    parts.push(n === 1 ? 'Limited to the first person.' : `Limited to the first ${n.toLocaleString('en-CA')} people.`);
  }
  return parts.join(' ');
}

export interface EmailKitInput {
  partnerName: string;
  /** The tag's active shared code, as stored (A-Z/0-9). */
  code: string;
  offer: OfferFields;
  expiresAt?: string | null;
  maxRedemptions?: number | null;
  /** For the expiry date; the admin's own zone when absent (the zone the code's end-of-day was set in). */
  timeZone?: string;
}

export type EmailKit = { ok: true; html: string; text: string; offer: string | null } | { ok: false; reason: string };

const FONT = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";
const LINK_STYLE = 'color:#1A3A1F;font-weight:600;';

/** The two versions of the kit; refuses a code that is not a stored code. */
export function buildEmailKit(input: EmailKitInput): EmailKit {
  const code = String(input.code ?? '');
  if (!CODE_RE.test(code)) return { ok: false, reason: 'No usable shared code: create the tag’s shared code first.' };
  const partner = String(input.partnerName ?? '').trim() || 'Our partner';
  const offer = offerLine(input.offer);
  const headline = offer ? `Get ${offer} with Coach Cedar — use code ${code}.` : `Join Coach Cedar with code ${code}.`;
  const fine = kitExpiryText(input.expiresAt, input.maxRedemptions, input.timeZone);

  const text = [
    `${partner} × Coach Cedar`,
    '',
    headline,
    '',
    '1. Install Coach Cedar:',
    `   iPhone: ${APP_STORE_URL}`,
    `   Android: ${PLAY_STORE_URL}`,
    '2. Sign up in the app.',
    `3. ${REDEEM_STEPS} and enter ${code}.`,
    ...(fine ? ['', fine] : []),
    '',
  ].join('\n');

  const e = escapeHtml;
  const htmlHeadline = offer
    ? `Get ${e(offer)} with Coach Cedar &mdash; use code <strong style="font-size:18px;letter-spacing:1px">${e(code)}</strong>.`
    : `Join Coach Cedar with code <strong style="font-size:18px;letter-spacing:1px">${e(code)}</strong>.`;
  const steps = e(REDEEM_STEPS).replace(/→/g, '&rarr;');
  const html = [
    `<!-- Coach Cedar offer for ${e(partner)}. Paste as-is: nothing here loads anything. -->`,
    `<div style="font:400 16px/1.5 ${FONT};color:#111827">`,
    `  <p style="margin:0 0 12px"><strong>${e(partner)} &times; Coach Cedar</strong></p>`,
    `  <p style="margin:0 0 12px">${htmlHeadline}</p>`,
    `  <ol style="margin:0 0 12px;padding-left:20px">`,
    `    <li>Install Coach Cedar: <a href="${e(APP_STORE_URL)}" style="${LINK_STYLE}">App Store (iPhone)</a> &middot; <a href="${e(PLAY_STORE_URL)}" style="${LINK_STYLE}">Google Play (Android)</a></li>`,
    `    <li>Sign up in the app.</li>`,
    `    <li>${steps} and enter <strong>${e(code)}</strong>.</li>`,
    `  </ol>`,
    ...(fine ? [`  <p style="margin:0;font-size:13px;color:#4b5563">${e(fine)}</p>`] : []),
    `</div>`,
    '',
  ].join('\n');

  return { ok: true, html, text, offer };
}
